'use client';
import Script from 'next/script';
import styles from './synchrony.module.scss';
import { useState, ChangeEvent, useEffect, useMemo } from 'react';
import {
  getFinancingOfferDetails,
  everydayFinancingOffersForCheckout,
  monthlyAmount,
  getCashbackFinanceOfferByMonths,
  isThreePercentPromoEligible,
} from '../utils/get-financing-offer-details';
import {
  QUALIFYING_OFFER,
  SYNCHRONY_DETAILS,
  lowerCase,
  TYPES,
  WMAP_PREFIX,
} from '../../constants';
import {
  extractMonthAndDate,
  //   TODO:: IN STORE Offers - <LIMITED></LIMITED>
  //   getCampaignRemainingCost,
  //   getValidSpecialCampaign,
  //   calculateFinancing,
} from '../utils/financing-offer-calculation-utils';
import { RichText } from '~/utils/rich-text';
import LearnHowSideDrawer from './checkout-synchrony-learn-how';
import { handleSynchronyPrequalifyTealium } from '~/utils/tealium/tealium-utag-link-events';
import { IPaymentConnector, IPaymentState } from '~/interfaces/payment';
import { PLAN_ID } from '~/constant/constants';
import {
  ProgressiveConfiguration,
  SynchronyConfiguration,
  FeatureFlag,
  Maybe,
} from '~/gql/__generated__/types';
import { ISalesOrderResponse } from '~/interfaces/get-sales-order';
import { useSetSyfDetails, wmapStore } from '~/store/wmap-store';
import { isServer } from '~/utils/host';
import { useRouter } from 'next/navigation';
import { generateCSId } from '~/utils/utils';
import CustomSkeleton from '~/components/custom-skeleton';
import ApplyInSite from '~/modules/payment/synchrony/components/apply';
import { getProgressiveTotal } from '../../progressive/utils';

export const learnHowCtaNameForCashback = 'Learn more';
type PaymentValue = {
  offerName: string;
  planID: string;
};

interface SynchronyProps {
  acceptContent: IPaymentConnector;
  checkoutCart: ISalesOrderResponse;
  checkoutConfigs: SynchronyConfiguration;
  progressiveConfigs: ProgressiveConfiguration;
  paymentState?: IPaymentState;
  featureFlags?: Maybe<Maybe<FeatureFlag>[]> | undefined;
}

type ApplyInSiteFlow = 'applyNow' | 'prequalify';
const Synchrony: React.FC<SynchronyProps> = ({
  acceptContent,
  checkoutCart,
  checkoutConfigs,
  progressiveConfigs,
  paymentState,
  featureFlags,
}) => {
  const [triggerFlow, setTriggerFlow] = useState<ApplyInSiteFlow | null>(null);
  const [loading, setLoading] = useState(false);
  const handleClick = (flow: ApplyInSiteFlow) => {
    setLoading(true);
    setTriggerFlow(null);

    setTimeout(() => {
      setTriggerFlow(flow);
    }, 0);
  };
  const setSyfDetails = useSetSyfDetails();
  const router = useRouter();
  const selectedOffer = getFinancingOfferDetails(
    checkoutConfigs,
    checkoutCart.balanceAmount,
  );

  const everyDayOffers = everydayFinancingOffersForCheckout(
    checkoutConfigs,
    checkoutCart.balanceAmount,
    true,
  );
  const applicableEveryDayOffers = (() => {
    const offerPlan24Months = everyDayOffers.find(
      (offer) => offer.planID === PLAN_ID.offer24,
    );
    return offerPlan24Months &&
      offerPlan24Months.minPurchase &&
      checkoutCart.balanceAmount < offerPlan24Months.minPurchase
      ? everyDayOffers.filter((offer) => offer.planID !== PLAN_ID.offer24)
      : everyDayOffers;
  })();

  const hasFirstEverydayOffer =
    applicableEveryDayOffers[applicableEveryDayOffers.length - 1];
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [selectedPayment, setSelectedPayment] = useState<string>(
    hasFirstEverydayOffer?.offerName || '',
  );
  const [isOfferLoading, setIsOfferLoading] = useState<boolean>(false);
  const [showOtherOffers, setShowOtherOffers] = useState(false);

  const payMonthly = monthlyAmount(selectedOffer, checkoutCart.balanceAmount);

  const progressiveContent = progressiveConfigs?.progressive;
  const progressiveTotalAmount = getProgressiveTotal(
    checkoutCart,
    (progressiveContent?.progressiveStateDeliveryFee || []).filter(
      (state): state is string => state !== null && state !== undefined,
    ),
    false,
  );

  const selectedEverydayOffer = useMemo(
    () =>
      applicableEveryDayOffers?.find((o) => o?.offerName === selectedPayment),
    [applicableEveryDayOffers, selectedPayment],
  );

  const displayOffers = useMemo(() => {
    if (applicableEveryDayOffers.length < 2) {
      return applicableEveryDayOffers;
    }
    const initialOffers = [
      applicableEveryDayOffers[applicableEveryDayOffers.length - 1],
      applicableEveryDayOffers[0],
    ];
    if (showOtherOffers) {
      const remaining = applicableEveryDayOffers.filter(
        (o) => !initialOffers.find((io) => io.offerName === o.offerName),
      );
      return initialOffers.concat(remaining);
    } else {
      return initialOffers;
    }
  }, [applicableEveryDayOffers, showOtherOffers]);
  const amountDue = checkoutCart?.AmountDue ?? 0;
  const offerMonths = selectedEverydayOffer?.numOfMonths ?? 0;
  const cashbackOffer = getCashbackFinanceOfferByMonths(
    checkoutConfigs?.financingOffers?.cashbackFinanceOffers,
    offerMonths,
  );
  const enable3PercentOffers =
    checkoutConfigs?.synchronyFinancingPurchaseRules?.enable3PercentOffers;

  const isExtraPromoEligible =
    !!cashbackOffer &&
    isThreePercentPromoEligible(
      amountDue,
      cashbackOffer.minPurchase ?? 0,
      enable3PercentOffers as number[],
      offerMonths,
    );

  useEffect(() => {
    setIsOfferLoading(false);
  }, [acceptContent.connectorHtml.AcceptPageContent]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    try {
      setIsOfferLoading(true);
      const value = JSON.parse(e.currentTarget.value) as PaymentValue;
      setSelectedPayment(value.offerName);
      const offerId = value.offerName?.replace(/\D/g, '');
      setSyfDetails({
        planOfferKey: `${value.planID},${offerId}`,
        offerName: value.offerName,
      });
      const url = new URL(window.location.href);
      url.searchParams.delete('error');
      router.replace(url.toString(), { scroll: false });
      router.refresh();
    } catch (error) {
      setIsOfferLoading(false);
      console.error('Invalid JSON value in input', error);
    }
  };

  const renderSynchronyScripts = () => {
    if (isServer) {
      // Return null or handle server-side case
      return null;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(
      acceptContent.connectorHtml.AcceptPageContent,
      'text/html',
    );
    const scripts = Array.from(doc.getElementsByTagName('script'));
    return scripts.map((script, index) => (
      <Script
        key={index}
        id={`aps-${index}`}
        src={script.src || undefined}
        dangerouslySetInnerHTML={
          script.src ? undefined : { __html: script.innerHTML }
        }
      />
    ));
  };

  //   TODO:: IN STORE Offers - LIMITED
  //   const RenderLimitedOffers = () => {
  //     if (!selectedOffer || !checkoutConfigs) return null;
  //     const campaign = getValidSpecialCampaign(selectedOffer);
  //     const remainingCost =
  //       campaign &&
  //       getCampaignRemainingCost(
  //         campaign,
  //         selectedOffer,
  //         checkoutCart.balanceAmount,
  //       );
  //     const { monthlyPayment } = calculateFinancing(
  //       checkoutCart.balanceAmount,
  //       selectedOffer.numOfMonths!,
  //       campaign,
  //     );
  //     if (campaign && remainingCost) {
  //       return (
  //         <div className={styles.limitedTimeCampaignSection}>
  //           <p className={styles.offerDetails} tabIndex={0}>
  //             {checkoutConfigs?.financingConfigurations?.limitedCampaignMessagePayment
  //               ?.replace('{limitedCampaignCost}', String(monthlyPayment))
  //               ?.replace(
  //                 '{validEndEndDate}',
  //                 String(campaign.campaignValidEndDate),
  //               )
  //               ?.replace('{numOfMonths}', `${remainingCost?.months}`)}
  //           </p>
  //           <div className={styles.learnHow}>
  //             <LearnHowSideDrawer
  //               checkoutOffer={selectedOffer}
  //               checkoutCart={checkoutCart}
  //               checkoutConfigs={checkoutConfigs}
  //               progressiveTotalAmount={progressiveTotalAmount}
  //             />
  //           </div>
  //         </div>
  //       );
  //     }
  //     return (
  //       <div className={styles.limitedTimeSection}>
  //         <div className={styles.learnHow}>
  //           <LearnHowSideDrawer
  //             checkoutOffer={selectedOffer}
  //             checkoutCart={checkoutCart}
  //             checkoutConfigs={checkoutConfigs}
  //             progressiveTotalAmount={progressiveTotalAmount}
  //           />
  //         </div>
  //       </div>
  //     );
  //   };

  const RenderEverydayOffers = () => {
    const minimumThreshold =
      checkoutConfigs?.synchronyFinancingPurchaseRules
        ?.minimumMonthlyPurchaseThreshold || 0;
    return (
      <div className={styles.everyDaySection}>
        {payMonthly >= minimumThreshold && (
          <div
            className={styles.supportingText}
            tabIndex={0}
            dangerouslySetInnerHTML={{
              __html: selectedEverydayOffer?.monthlyOfferDetails || '',
            }}
          />
        )}
        {isExtraPromoEligible && (
          <>
            <div
              className={styles.supportingText}
              dangerouslySetInnerHTML={{
                __html: cashbackOffer?.financingOfferDataOnPage || '',
              }}
            />
            <a
              className={styles.buttonLink}
              href={cashbackOffer?.learnMoreLink?.linkUrl || ''}
              target={
                cashbackOffer?.learnMoreLink?.openInNewTab ? '_blank' : '_self'
              }
              rel="noopener noreferrer"
            >
              {cashbackOffer?.learnMoreLink?.ariaLabel}
            </a>
          </>
        )}
        <p className={styles.supportingText} tabIndex={0}>
          {QUALIFYING_OFFER}
        </p>

        {displayOffers.map((offer, index) => (
          <div key={index}>
            <div
              className={`${styles.radioSelectWrapper} ${selectedPayment === offer.offerName ? styles.radioLabelActive : styles.radioLabelInactive}`}
            >
              <input
                className={styles.radioButton}
                type="radio"
                id={generateCSId(
                  `${WMAP_PREFIX}-${lowerCase(TYPES.SYNCHRONY)}`,
                  offer?.offerName,
                  `option-${index}`,
                )}
                name="payment"
                value={JSON.stringify({
                  offerName: offer.offerName,
                  planID: offer.planID,
                })}
                checked={selectedPayment === offer.offerName}
                onChange={handleChange}
              />
              <label
                id={generateCSId(
                  `${WMAP_PREFIX}-${lowerCase(TYPES.SYNCHRONY)}`,
                  offer?.offerName,
                  index,
                )}
                htmlFor={generateCSId(
                  `${WMAP_PREFIX}-${lowerCase(TYPES.SYNCHRONY)}`,
                  offer?.offerName,
                  `option-${index}`,
                )}
                className={`${styles.radioLabel} ${selectedPayment === offer.offerName ? styles.radioLabelActive : styles.radioLabelInactive}`}
                tabIndex={0}
              >
                {offer.offerName}
              </label>
              {payMonthly < minimumThreshold && (
                <p
                  id={generateCSId(
                    `${WMAP_PREFIX}`,
                    `${lowerCase(TYPES.SYNCHRONY)}-min-threshold`,
                    index,
                  )}
                  className={styles.minimumThreshold}
                >
                  {
                    checkoutConfigs?.synchronyFinancingPurchaseRules
                      ?.lowCostOfferDetailsOnCart
                  }
                </p>
              )}
              {checkoutConfigs && (
                <div className={styles.learnMore}>
                  <LearnHowSideDrawer
                    checkoutOffer={offer}
                    checkoutCart={checkoutCart}
                    checkoutConfigs={checkoutConfigs}
                    progressiveTotalAmount={progressiveTotalAmount}
                    isExtraPromoEligible={isExtraPromoEligible}
                    paymentState={paymentState}
                    isOfferLoading={isOfferLoading}
                  />
                </div>
              )}
            </div>
          </div>
        ))}
        {!showOtherOffers && applicableEveryDayOffers.length > 2 && (
          <button
            id={`${WMAP_PREFIX}-see-other-offers-btn`}
            type="button"
            className={styles.loadMoreButton}
            onClick={() => setShowOtherOffers(true)}
          >
            See other offers
          </button>
        )}
        {isOfferLoading && (
          <div className={styles.loaderSection}>
            <CustomSkeleton height="50px" width="166px" />
            <CustomSkeleton height="50px" width="166px" />
          </div>
        )}
      </div>
    );
  };

  const RenderSpecialAndCashbackOffers = () => {
    if (!selectedOffer || !checkoutConfigs) return null;

    const endDate = extractMonthAndDate(selectedOffer.endDate);
    const nameForCashback = checkoutConfigs.financingConfigurations
      .cashbackFinancingEnable
      ? learnHowCtaNameForCashback
      : undefined;

    return (
      <div className={styles.section}>
        <RichText
          className={styles.infoCashback}
          content={
            selectedOffer.checkoutDisplay &&
            'specialOffer' in selectedOffer.checkoutDisplay
              ? selectedOffer.checkoutDisplay.specialOffer
              : undefined
          }
        />
        <p className={styles.subtitleCashback}>{QUALIFYING_OFFER}</p>
        <RichText
          className={styles.descriptionCashback}
          content={selectedOffer.checkoutDisplay?.heading}
        />
        <RichText
          className={styles.additionalInfoCashback}
          content={selectedOffer.checkoutDisplay?.details}
        />
        <div className={styles.detailsButton}>
          <LearnHowSideDrawer
            checkoutOffer={selectedOffer}
            checkoutCart={checkoutCart}
            checkoutConfigs={checkoutConfigs}
            learnHowCtaNameForCashback={nameForCashback}
            progressiveTotalAmount={progressiveTotalAmount}
            paymentState={paymentState}
            featureFlags={featureFlags}
            isOfferLoading={isOfferLoading}
          />
        </div>
        <p className={styles.offerValidity} role="note" id="offerValidNote">
          {endDate && `${SYNCHRONY_DETAILS.OFFER_VALID_UNTIL} ${endDate}`}
        </p>
      </div>
    );
  };

  const renderContent = () => {
    if (
      checkoutConfigs?.financingConfigurations?.specialFinancingEnable ||
      selectedOffer?.isCashBack
    ) {
      return <RenderSpecialAndCashbackOffers />;
    }
    //   TODO:: IN STORE Offers - LIMITED
    // else if (selectedOffer?.isLTO) {
    //   return <RenderLimitedOffers />;
    // }
    else {
      return <RenderEverydayOffers />;
    }
  };

  const { applyNowLink, synchronyButtonText, synchronySwitch } =
    acceptContent.config;
  const synchronyButtonID = generateCSId(
    WMAP_PREFIX,
    `-${lowerCase(TYPES.SYNCHRONY)}-${synchronyButtonText}`,
    'btn',
  );
  useEffect(() => {
    const container = document.getElementById('synchrony-cta-container');
    if (!container) return;

    const clickHandler = (e: Event) => {
      const target = e.target as HTMLElement;
      if (target?.id === synchronyButtonID) {
        handleClick('prequalify');
        handleSynchronyPrequalifyTealium();
      }
    };

    container.addEventListener('click', clickHandler);

    return () => {
      container.removeEventListener('click', clickHandler);
    };
  }, [synchronyButtonID]);

  const { AcceptPageContent } = acceptContent.connectorHtml;
  const synchronySwitchOnMarkup = `<div class="msax-Control"><div class="sync-cta-wrapper"><button type="button" class="msax-ApplyNow" id="ApplyNowBtn" onclick="window.open('${applyNowLink}')" tabindex="0"> Apply Now</button></div></div>`;
  const sanitizedHtml = AcceptPageContent
    ? sanitizeHtml(AcceptPageContent)
    : '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(sanitizedHtml, 'text/html');

  setInputValue(doc, 'custAddress1', checkoutCart.Street);
  setInputValue(doc, 'custState', checkoutCart.ISOStateCode);
  setInputValue(doc, 'custZIPCode', checkoutCart.ZipCode);
  setInputValue(doc, 'transAmount1', String(checkoutCart.balanceAmount));
  const finalHtml = doc.body.innerHTML;
  const loaderHtml = `<div class="${styles.loaderContainer}" style="height:21px; width:21px; color:${'#ffffff'}"><div></div><div></div><div></div><div></div></div>`;
  const synchronySwitchOffMarkup = `<div class="msax-Control"><div class="sync-cta-wrapper sync-cta-prequalify"><div class="msax-hidden">${finalHtml}</div></div><div><button type="button" class="msax-ApplyNow msax-prequalify ${loading && 'loading'}" id=${synchronyButtonID} tabindex="0"> ${loading ? loaderHtml : synchronyButtonText}</button><div/></div>`;
  const content = synchronySwitch
    ? synchronySwitchOnMarkup
    : synchronySwitchOffMarkup;

  useEffect(() => {
    setHtmlContent(content);
  }, [content]);

  const formatKey = (offer: typeof selectedOffer) =>
    `${offer?.planID},${offer?.offerName?.replace(/\D/g, '') ?? ''}`;

  useEffect(() => {
    const allOffers = [selectedOffer, ...applicableEveryDayOffers].filter(
      Boolean,
    );
    const { planOfferKey } = wmapStore.getState().syfDetails || {};
    // Try to use the preselected offer
    const preselectedOffer =
      planOfferKey &&
      allOffers.find((offer) => formatKey(offer) === planOfferKey);

    // If no preselected offer exists, then fall back to the default offer
    const defaultOffer =
      selectedOffer?.isCashBack ||
      //   TODO:: IN STORE Offers - LIMITED
      //   selectedOffer?.isLTO ||
      checkoutConfigs?.financingConfigurations?.specialFinancingEnable
        ? selectedOffer
        : applicableEveryDayOffers?.[applicableEveryDayOffers.length - 1];

    const finalOffer = preselectedOffer || defaultOffer;
    if (!finalOffer) return;

    setSelectedPayment(finalOffer.offerName ?? '');
    setSyfDetails({
      planOfferKey: formatKey(finalOffer),
      offerName: finalOffer.offerName ?? '',
    });
  }, [applicableEveryDayOffers, checkoutConfigs, selectedOffer]);

  return (
    <div className={styles.container} tabIndex={0} aria-label="synchrony">
      {renderContent()}
      {renderSynchronyScripts()}
      <div className={styles.prequalifyContainer}>
        <div
          id="synchrony-cta-container"
          dangerouslySetInnerHTML={{
            __html: htmlContent,
          }}
        />
        {triggerFlow && (
          <ApplyInSite
            triggerFlow={triggerFlow}
            orderId={checkoutCart.QuotationId || checkoutCart.SalesId}
            setLoadingAction={setLoading}
          />
        )}
      </div>
    </div>
  );
};

function sanitizeHtml(html: string): string {
  return html.replace(
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    '',
  );
}

function setInputValue(doc: Document, id: string, value: string): void {
  const input = doc.getElementById(id);
  if (input instanceof HTMLInputElement) {
    input.value = value;
  }
}

export default Synchrony;
